import { useEffect } from "react"
import { useUIStore } from "@/stores/uiStore"

export function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch(() => {})
  } else {
    document.exitFullscreen().catch(() => {})
  }
}

/**
 * Mount once (in WorkspaceShell). Owns fullscreenchange listener + F-key handler.
 * Writes canonical state to uiStore.isFullscreen.
 * Returns { isFullscreen, toggle } for the mounting component's convenience.
 */
export function useFullscreen() {
  const isFullscreen = useUIStore((s) => s.isFullscreen)
  const setFullscreen = useUIStore((s) => s.setFullscreen)

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement)

    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== "F") return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA") return
      e.preventDefault()
      toggleFullscreen()
    }

    document.addEventListener("fullscreenchange", onChange)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("fullscreenchange", onChange)
      document.removeEventListener("keydown", onKey)
    }
  }, [setFullscreen])

  return { isFullscreen, toggle: toggleFullscreen }
}
